import {
    Address,
    Cart,
    Order,
    PaymentSession,
    ShippingOption,
} from '@medusajs/medusa'
import { assign, fromPromise, setup } from 'xstate'

export type CartMachineContext = {
    order_placed: Order | null
    cart: Omit<Cart, 'refundable_amount' | 'refunded_total'> | null
    payment_session: PaymentSession | null
    options: {
        anonymous_mode: boolean
        anonymous_email: string | null
        email_already_exists: boolean
        shipping_address: Address | null
        shipping_methods: {
            store_id: string
            shipping_option: ShippingOption
        }[]
    }
}

export type CartMachineEvent =
    | { type: 'RESET' }
    | { type: 'RETRY' }
    | { type: 'AUTH.SIGN_IN' }
    | { type: 'AUTH.REGISTER' }
    | { type: 'AUTH.ANONYMOUS' }
    | { type: 'NEXT' }
    | { type: 'ERROR'; error: Error }
    | { type: 'UPDATE_CART'; updatedCart: Cart | null }
    | { type: 'UPDATE_ORDER'; updatedOrder: Order | null }
    | { type: 'UPDATE_OPTIONS'; updatedOptions: CartMachineContext['options'] }
    | {
          type: 'UPDATE_PAYMENT_SESSION'
          updatedPaymentSession: PaymentSession | null
      }
    | { type: 'UPDATE_ANONYMOUS_MODE'; updatedAnonymousMode: boolean }
    | { type: 'UPDATE_ANONYMOUS_EMAIL'; updatedAnonymousEmail: string | null }
    | { type: 'EMAIL_EXISTS' } 
    | { type: 'GO_TO_LOGIN' }   

export const cartMachine = setup({
    types: {
        context: {} as CartMachineContext,
        events: {} as CartMachineEvent,
    },
    actions: {
        assignToCart: assign({
            cart: ({ event }) =>
                (event as Extract<CartMachineEvent, { type: 'UPDATE_CART' }>)
                    .updatedCart,
        }),
        assignToOrder: assign({
            order_placed: ({ event }) =>
                (event as Extract<CartMachineEvent, { type: 'UPDATE_ORDER' }>)
                    .updatedOrder,
        }),
        assignToOptions: assign({
            options: ({ event }) =>
                (
                    event as Extract<
                        CartMachineEvent,
                        { type: 'UPDATE_OPTIONS' }
                    >
                ).updatedOptions,
        }),
        assignToPaymentSession: assign({
            payment_session: ({ event }) =>
                (
                    event as Extract<
                        CartMachineEvent,
                        { type: 'UPDATE_PAYMENT_SESSION' }
                    >
                ).updatedPaymentSession,
        }),
        assignToAnonymousMode: assign({
            options: ({ context, event }) => ({
                ...context.options,
                anonymous_mode: (
                    event as Extract<
                        CartMachineEvent,
                        { type: 'UPDATE_ANONYMOUS_MODE' }
                    >
                ).updatedAnonymousMode,
            }),
        }),
        assignToAnonymousEmail: assign({
            options: ({ context, event }) => ({
                ...context.options,
                anonymous_email: (
                    event as Extract<
                        CartMachineEvent,
                        { type: 'UPDATE_ANONYMOUS_EMAIL' }
                    >
                ).updatedAnonymousEmail,
            }),
        }),
        enterAnonymousMode: assign({
            options: ({ context }) => ({
                ...context.options,
                anonymous_mode: true,
            }),
        }),
        exitAnonymousMode: assign({
            options: ({ context }) => ({
                ...context.options,
                anonymous_mode: false,
            }),
        }),
        assignEmailAlreadyExists: assign({
            options: ({ context }) => ({
                ...context.options,
                email_already_exists: true,
            }),
        }),
        resetEmailAlreadyExists: assign({
            options: ({ context }) => ({
                ...context.options,
                email_already_exists: false,
            }),
        }),
    },
    actors: {
        initializeCheckout: fromPromise<void, CartMachineContext>(
            async () => {},
        ),
        syncShippingOptions: fromPromise<void, CartMachineContext>(
            async () => {},
        ),
        createAndSelectPaymentSession: fromPromise<void, CartMachineContext>(
            async () => {},
        ),
    },
    guards: {
        isUserLoggedIn: () => false,
    },
}).createMachine({
    id: 'cart',
    initial: 'IDLE',
    on: {
        RESET: '.IDLE',
        ERROR: '.FAILURE',
        UPDATE_CART: {
            actions: 'assignToCart',
        },
        UPDATE_ORDER: {
            actions: 'assignToOrder',
        },
        UPDATE_OPTIONS: {
            actions: 'assignToOptions',
        },
        UPDATE_PAYMENT_SESSION: {
            actions: 'assignToPaymentSession',
        },
        UPDATE_ANONYMOUS_MODE: {
            actions: 'assignToAnonymousMode',
        },
        UPDATE_ANONYMOUS_EMAIL: {
            actions: 'assignToAnonymousEmail',
        },
    },
    context: {
        order_placed: null,
        cart: null,
        payment_session: null,
        options: {
            anonymous_mode: false,
            anonymous_email: null,
            shipping_address: null,
            shipping_methods: [],
            email_already_exists: false
        },
    },
    states: {
        IDLE: {
            on: {
                NEXT: [
                    {
                        target: 'CHECKOUT.INITIALIZING',
                        guard: 'isUserLoggedIn',
                    },
                    {
                        target: '#cart.AUTH_GATEWAY.SIGN_IN',
                    },
                ],
            },
        },
        AUTH_GATEWAY: {
            initial: 'SIGN_IN',
            states: {
                SIGN_IN: {
                    id: 'SIGN_IN',
                    entry: 'exitAnonymousMode',
                    on: {
                        NEXT: '#cart.CHECKOUT.INITIALIZING',
                        'AUTH.REGISTER': '#cart.AUTH_GATEWAY.REGISTER',
                        'AUTH.ANONYMOUS': '#cart.AUTH_GATEWAY.ANONYMOUS',
                    },
                },
                REGISTER: {
                    id: 'REGISTER',
                    entry: 'exitAnonymousMode',
                    on: {
                        NEXT: '#cart.CHECKOUT.INITIALIZING',
                        'AUTH.SIGN_IN': '#cart.AUTH_GATEWAY.SIGN_IN',
                    },
                },
                ANONYMOUS: {
                    id: 'ANONYMOUS',
                    entry: 'enterAnonymousMode',
                     on: {
                        NEXT: {
                            target: '#cart.CHECKOUT.INITIALIZING',
                            actions: 'resetEmailAlreadyExists', // 👈 reset au prochain essai
                        },
                        'AUTH.SIGN_IN': {
                            target: '#cart.AUTH_GATEWAY.SIGN_IN',
                            actions: 'resetEmailAlreadyExists', // 👈
                        },
                    },
                },
            },
        },
        CHECKOUT: {
            initial: 'INITIALIZING',
            states: {
                INITIALIZING: {
                    invoke: {
                        src: 'initializeCheckout',
                        input: ({ context }) => context,
                        onDone: '#cart.CHECKOUT.SHIPPING.CUSTOMER_DETAILS',
                         onError: [
                        {
                            guard: ({ event }) => {
                                const error = event.error as any
                                return error?.code === 'EMAIL_ALREADY_EXISTS'
                            },
                            target: '#cart.AUTH_GATEWAY.ANONYMOUS',
                            actions: 'assignEmailAlreadyExists',
                        },
                        {
                            target: '#cart.FAILURE',
                        }
                    ],
                    },
                },
                SHIPPING: {
                    initial: 'CUSTOMER_DETAILS',
                    states: {
                        CUSTOMER_DETAILS: {
                            on: {
                                NEXT: '#cart.CHECKOUT.SHIPPING.ORDER_DETAILS',
                            },
                        },
                        ORDER_DETAILS: {
                            on: {
                                RETRY: '#cart.CHECKOUT.SHIPPING.CUSTOMER_DETAILS',
                                NEXT: '#cart.CHECKOUT.DETAILS',
                            },
                        },
                    },
                },
                DETAILS: {
                    initial: 'SYNCING',
                    states: {
                        SYNCING: {
                            invoke: {
                                src: 'syncShippingOptions',
                                input: ({ context }) => context,
                                onDone: '#cart.CHECKOUT.DETAILS.SUMMARY',
                                onError: '#cart.FAILURE',
                            },
                        },
                        SUMMARY: {
                            on: {
                                NEXT: '#cart.CHECKOUT.PAYMENT',
                            },
                        },
                    },
                },
                PAYMENT: {
                    initial: 'INITIALIZING',
                    states: {
                        INITIALIZING: {
                            invoke: {
                                src: 'createAndSelectPaymentSession',
                                input: ({ context }) => context,
                                onDone: '#cart.CHECKOUT.PAYMENT.PAYMENT_FORM',
                                onError: '#cart.FAILURE',
                            },
                        },
                        PAYMENT_FORM: {
                            on: {
                                NEXT: '#cart.CHECKOUT.PAYMENT.CONFIRMATION',
                            },
                        },
                        CONFIRMATION: {
                            after: {
                                3000: '#cart.SUCCESS',
                            },
                        },
                    },
                },
            },
        },
        SUCCESS: {
            type: 'final',
        },
        FAILURE: {
            type: 'final',
        },
    },
})
